import {
  Col,
  Image,
  Input,
  InputNumber,
  List,
  message,
  notification,
  Row,
  Select,
  Space,
  Tabs,
  Tooltip,
  Upload,
} from 'antd'
import {
  Button,
  ButtonActionBar,
  DeleteAndUpdate,
  InputCell,
  SelectCell,
  Typography,
} from 'modules/core/components'
import { useQuery } from 'modules/core/customHooks'
import { EditOutlined, GlobalOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { connect } from 'react-redux'
import {
  generateMessageError,
  generateSelectOptions,
  isUserAllowed,
  setLangUtils,
  setNumeralUtils,
} from 'modules/core/utils'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import configuration from 'modules/configuration'
import _ from 'lodash'
import login from 'modules/login'
import { ROLES } from 'modules/core/constants'
import PropTypes from 'prop-types'

const { SETTINGS__SECURITY__ROLES__ABM, SETTINGS__SECURITY__USERS__ABM } = ROLES

const TabsData = ({
  pkid,
  language,
  userDetail,
  rolesList,
  areaList,
  userProfile,
  setShowUploadModal,
  society_id,
  onRefetch,
  setShowDrawer,
  setIsSaving,
  isSaving,
  assignRole,
  editUser,
  editProfile,
  whoAmI,
  setFormatNumbers,
  editFormatNumber,
  format_number,
  type,
}) => {
  const [rowId, setRowId] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const [fileList, setFileList] = useState([])
  const [showEdit, setShowEdit] = useState(false)
  const [lang, setLang] = useState(language)
  const [dataFormat, setDataFormat] = useState({})
  const [showFormatEdit, setShowFormatEdit] = useState(false)
  let { userId } = useParams()
  let query = useQuery()
  const { t } = useTranslation()

  const onCellEdit = (name, value) => setDataCellEdit({ ...dataCellEdit, [`${name}`]: value })

  const onCancel = () => setRowId(null)

  const renderDataAreas = () => {
    if (userDetail.approval_areas.length > 0) {
      return userDetail.approval_areas
        .map((el) => (areaList.length > 0 ? areaList.find((area) => area.id === el).name : ''))
        .join(', ')
    } else {
      return ''
    }
  }

  const renderDataPhone = () => {
    if (pkid === userDetail.id) {
      return userProfile?.phone_number
    } else {
      return ''
    }
  }

  const renderDataNick = () => {
    if (pkid === userDetail.id) {
      return userProfile?.nickname
    } else {
      return ''
    }
  }

  const NAMES = {
    email: {
      id: 'email',
      title: t('FIELD_EMAIL'),
      data: userDetail.email,
      isInput: true,
      isEditable: true,
      placeholder: t('ACTION_ENTER'),
    },
    first_name: {
      id: 'first_name',
      title: t('FIELD_NAME'),
      data: userDetail.first_name,
      isInput: true,
      isEditable: true,
      placeholder: t('ACTION_ENTER'),
    },
    last_name: {
      id: 'last_name',
      title: t('FIELD_SURNAME'),
      data: userDetail.last_name,
      isInput: true,
      isEditable: true,
      placeholder: t('ACTION_ENTER'),
    },
    roles: {
      id: 'roles',
      title: t('LABEL_ROLE'),
      options: rolesList.data,
      extraData: userDetail.roles.map((el) => el.id),
      data: userDetail.roles.map((el) => el.name).join(', '),
      isInput: false,
      isEditable: true,
      placeholder: t('ACTION_SELECT'),
    },
    approval_areas: {
      id: 'approval_areas',
      title: t('LABEL_ASSOCIATED_AREAS'),
      data: renderDataAreas(),
      isInput: false,
      isEditable: false,
      placeholder: t('ACTION_SELECT'),
    },
    ...(pkid === userDetail.id
      ? {
          phone_number: {
            id: 'phone_number',
            title: t('LABEL_PHONE'),
            data: renderDataPhone(),
            isInput: true,
            isEditable: true,
            placeholder: '+549 221 9999999',
          },
          nickname: {
            id: 'nickname',
            title: t('LABEL_NICKNAME'),
            data: renderDataNick(),
            isInput: true,
            isEditable: true,
            placeholder: t('ACTION_ENTER'),
          },
        }
      : {}),
  }

  const renderComponent = (item) =>
    item.isInput ? (
      <InputCell
        initialValue={item.data}
        onCellUpdate={onCellEdit}
        name={item.id}
        placeholder={item.placeholder}
      />
    ) : (
      <SelectCell
        initialValue={item.extraData}
        name={item.id}
        onCellUpdate={onCellEdit}
        mode="multiple"
        maxTagCount={1}
        maxTagTextLength={8}
        showArrow
        allowClear
        style={{ width: 250 }}
        options={item.options}
      />
    )

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  const onAddUploadFile = (file) => {
    setFileList([file])
    return false
  }

  const handleEdit = () => {
    const { nickname, phone_number, roles, ...restProps } = dataCellEdit
    setIsSaving(true)
    Promise.all([
      editUser(userId, { society: society_id, ...restProps }),
      roles && assignRole(userId, { role_ids: roles }),
      (nickname || phone_number) && editProfile(userId, { nickname, phone_number }),
    ])
      .then(() => {
        onRefetch()
        message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
        setIsSaving(false)
        setRowId(null)
        setDataCellEdit({})
      })
      .catch((error) => {
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error, 'FEEDBACK_SAVE_CHANGES_FAIL'),
          duration: 0,
        })
        setIsSaving(false)
        setRowId(null)
        setDataCellEdit({})
      })
  }

  const renderButtons = (item) => {
    if (item.isEditable) {
      if (showEdit && rowId === item.id) {
        return (
          <DeleteAndUpdate
            onCancel={onCancel}
            onConfirm={onCancel}
            onSave={() => handleEdit()}
            dataCellEdit={dataCellEdit}
            loading={false}
          />
        )
      } else {
        return (
          <Button.Icon
            onClick={() => {
              setShowEdit(true)
              setRowId(item.id)
            }}
            disabled={
              (item.id === 'roles' && !isUserAllowed(SETTINGS__SECURITY__ROLES__ABM)) ||
              (item.id === 'email' && !isUserAllowed(SETTINGS__SECURITY__USERS__ABM))
            }
            icon={<Typography.Icon icon={EditOutlined} />}
          />
        )
      }
    }
  }

  const handleEditPhoto = () => {
    setIsSaving(true)
    const formData = new FormData()
    fileList.forEach((file) => {
      formData.append('photo', file['originFileObj'])
    })
    editProfile(userId, formData)
      .then(() => {
        onRefetch()
        message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
        setIsSaving(false)
      })
      .catch((error) => {
        setIsSaving(false)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const renderuserData = () => {
    const { id, full_name, status, society_pk, society_name, groups, quicksight, ...restProps } =
      userDetail
    const data = {
      ...restProps,
      ...(pkid === userDetail.id && {
        nickname: userProfile?.nickname,
        phone_number: userProfile?.phone_number,
      }),
    }

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {pkid === userDetail.id && (
          <ButtonActionBar position="space-between">
            <Typography.Headline type="primary" level={4}>
              {t('CONFIG_PROFILE_PERSONAL_DATA_TAB')}
            </Typography.Headline>
            <Button.Primary title="ACTION_CONFIGURATE" onClick={() => setShowDrawer(true)} />
          </ButtonActionBar>
        )}
        {pkid === userDetail.id && (
          <>
            <Space direction="vertical">
              <Typography.Subtitle>{t('CONFIG_PROFILE_PHOTO_TITLE')}</Typography.Subtitle>
              <Typography.Body level={2}>
                {t('CONFIG_PROFILE_SELECT_MODIFY_PHOTO_SUBTITLE')}
              </Typography.Body>
            </Space>
            <Space direction="horizontal">
              {userProfile?.photo && (
                <>
                  <Space direction="vertical">
                    <Image width={100} src={userProfile?.photo} />
                    <Typography.Body level={2}>
                      {t('CONFIG_PROFILE_IMG_FORMAT_TYPOGRAPHY_TEXT')}
                    </Typography.Body>
                  </Space>
                  <Button.Icon
                    title="ACTION_EDIT"
                    icon={<Typography.Icon icon={EditOutlined} />}
                    onClick={() => setShowUploadModal(true)}
                  />
                </>
              )}
              {!userProfile?.photo && (
                <>
                  <Space direction="vertical">
                    <Upload
                      listType="picture-card"
                      accept=".jpg, .png"
                      fileList={fileList}
                      onChange={onChange}
                      beforeUpload={onAddUploadFile}
                    >
                      {fileList.length < 1 && `+ ${t('ACTION_UPLOAD')}`}
                    </Upload>
                    <Typography.Body level={2}>
                      {t('CONFIG_PROFILE_IMG_FORMAT_TYPOGRAPHY_TEXT')}
                    </Typography.Body>
                  </Space>
                  {fileList.length > 0 && (
                    <Button.Primary title="ACTION_SAVE" onClick={() => handleEditPhoto()} />
                  )}
                </>
              )}
            </Space>
          </>
        )}
        <List
          itemLayout="horizontal"
          dataSource={_.keys(data)}
          loading={isSaving}
          renderItem={(item) => (
            <List.Item key={item.id}>
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space direction="horizontal">
                      <Typography.Subtitle>{`${NAMES[item].title}:`}</Typography.Subtitle>
                      {showEdit && rowId === NAMES[item].id ? (
                        renderComponent(NAMES[item])
                      ) : (
                        <Typography.Body>
                          {NAMES[item].data || t('STATE_TAG_UNDEFINED')}
                        </Typography.Body>
                      )}
                    </Space>
                    {pkid === userDetail.id && renderButtons(NAMES[item])}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Space>
    )
  }

  const LANGUAJE_OPTIONS = [
    {
      id: 'ES',
      name: t('ES'),
    },
    {
      id: 'EN',
      name: t('EN'),
    },
  ]

  const handleOnSave = () => {
    setIsSaving(true)
    editProfile(pkid, { language: lang })
      .then((response) => {
        whoAmI().then(() => {
          setLangUtils(lang)
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
        })
      })
      .catch((error) => {
        setIsSaving(false)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const DELIMITER_OPTIONS = [
    {
      id: '.',
      name: '.',
    },
    {
      id: ',',
      name: ',',
    },
  ]

  const handleFormatEdit = () => {
    const action = () =>
      !format_number.format_enable
        ? setFormatNumbers({ ...dataFormat, society: society_id, user: pkid })
        : editFormatNumber(format_number.id, {
            ...format_number,
            ...dataFormat,
            society: society_id,
            user: pkid,
          })
    setIsSaving(true)
    action()
      .then(() => {
        whoAmI().then((response) => {
          setNumeralUtils(response.payload.data)
          setShowFormatEdit(false)
          setIsSaving(false)
          setDataFormat({})
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
        })
      })
      .catch((error) => {
        setShowFormatEdit(false)
        setIsSaving(false)
        setDataFormat({})
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const renderDefault = () => {
    if (query.get('tab') !== null) {
      return query.get('tab')
    } else {
      return 'languaje'
    }
  }

  const renderShowButtons = () => {
    if (showFormatEdit) {
      return (
        <Space direction="horizontal">
          <Button.Default
            title="ACTION_CANCEL"
            disabled={isSaving}
            onClick={() => setShowFormatEdit(false)}
          />
          <Button.Primary title="ACTION_SAVE" onClick={() => handleFormatEdit()} />
        </Space>
      )
    } else {
      return (
        <Button.Icon
          title="ACTION_EDIT"
          loading={isSaving}
          icon={<Typography.Icon icon={EditOutlined} />}
          onClick={() => setShowFormatEdit(true)}
        />
      )
    }
  }

  const renderDefaultValueSelect = (format) => {
    if (format_number.format_enable) {
      return format
    } else {
      return undefined
    }
  }

  const renderActionsThousands = () => {
    if (showFormatEdit) {
      return (
        <Select
          options={generateSelectOptions({ options: DELIMITER_OPTIONS })}
          placeholder={t('ACTION_SELECT')}
          style={{ width: 100 }}
          defaultValue={renderDefaultValueSelect(format_number.delimiter_thousand)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, delimiter_thousand: val }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.delimiter_thousand}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsDecimal = () => {
    if (showFormatEdit) {
      return (
        <Select
          options={generateSelectOptions({ options: DELIMITER_OPTIONS })}
          placeholder={t('ACTION_SELECT')}
          style={{ width: 100 }}
          defaultValue={renderDefaultValueSelect(format_number.delimiter_decimal)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, delimiter_decimal: val }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.delimiter_decimal}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsCantDecimal = () => {
    if (showFormatEdit) {
      return (
        <InputNumber
          min={0}
          max={4}
          defaultValue={format_number.format_enable ? format_number.decimal_places_to_show : 0}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, decimal_places_to_show: val }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.decimal_places_to_show}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsAbbThousand = () => {
    if (showFormatEdit) {
      return (
        <Input
          placeholder="Ingresar"
          defaultValue={renderDefaultValueSelect(format_number.abbreviation_thousand)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, abbreviation_thousand: val.target.value }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.abbreviation_thousand}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsAbbMillon = () => {
    if (showFormatEdit) {
      return (
        <Input
          placeholder="Ingresar"
          defaultValue={renderDefaultValueSelect(format_number.abbreviation_million)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, abbreviation_million: val.target.value }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.abbreviation_million}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsAbbBillon = () => {
    if (showFormatEdit) {
      return (
        <Input
          placeholder="Ingresar"
          defaultValue={renderDefaultValueSelect(format_number.abbreviation_billion)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, abbreviation_billion: val.target.value }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.abbreviation_billion}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  const renderActionsAbbTrillon = () => {
    if (showFormatEdit) {
      return (
        <Input
          placeholder="Ingresar"
          defaultValue={renderDefaultValueSelect(format_number.abbreviation_trillion)}
          onChange={(val) =>
            setDataFormat((prevState) => {
              return { ...prevState, abbreviation_trillion: val.target.value }
            })
          }
        />
      )
    } else if (format_number.format_enable) {
      return <Typography.Body>{format_number.abbreviation_trillion}</Typography.Body>
    } else {
      return <Typography.Body>{t('STATE_TAG_UNDEFINED')}</Typography.Body>
    }
  }

  return (
    <Tabs size="small" defaultActiveKey={type || 'userData'}>
      <Tabs.TabPane
        tab={t('CONFIG_PROFILE_PERSONAL_DATA_TAB')}
        key="userData"
        style={{ padding: '0px 24px' }}
      >
        {renderuserData()}
      </Tabs.TabPane>
      {pkid === userDetail.id && (
        <Tabs.TabPane tab={t('CONFIG_SETTINGS_MODAL_TITLE')} key="preferences">
          <Tabs size="small" tabPosition="left" defaultActiveKey={renderDefault()}>
            <Tabs.TabPane tab={t('FIELD_LANGUAJE')} key="languaje">
              <Typography.Headline level={4}>{t('FIELD_LANGUAJE')}</Typography.Headline>
              <Space direction="vertical">
                <Typography.Body>
                  {t('CONFIG_USERS_SELECT_LANGUAJE_TYPOGRAPHY_BODY')}
                </Typography.Body>
                <Space direction="horizontal">
                  <Select
                    style={{ width: 200 }}
                    defaultValue={lang}
                    suffixIcon={<GlobalOutlined />}
                    onChange={(val) => setLang(val)}
                    options={generateSelectOptions({ options: LANGUAJE_OPTIONS })}
                  />
                  {language !== lang && (
                    <Button.Primary title="ACTION_SAVE" onClick={handleOnSave} />
                  )}
                </Space>
              </Space>
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('FIELD_FORMAT')} key="format">
              <Space direction="vertical">
                <Space direction="horizontal">
                  <Tooltip title={t('CONFIG_USERS_FORMAT_INFORMATIVE_ICON_TOOLTIP_TITLE')}>
                    <Typography.Icon level={2} icon={InfoCircleOutlined} />
                  </Tooltip>
                  <Typography.Headline level={4}>{t('FIELD_FORMAT')}</Typography.Headline>
                  {renderShowButtons()}
                </Space>
                <Row gutter={[24, 24]} justify="start">
                  <Col span={24}>
                    <Typography.Body>
                      <strong>{t('LABEL_DELIMITERS')}</strong>
                    </Typography.Body>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_DELIMITER_THOUSANDS')}:`}</Typography.Body>
                      {renderActionsThousands()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_DELIMITER_DECIMAL')}:`}</Typography.Body>
                      {renderActionsDecimal()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Tooltip title={t('CONFIG_USERS_DECIMAL_INFORMATIVE_ICON_TOOLTIP_TITLE')}>
                        <Typography.Icon level={2} icon={InfoCircleOutlined} />
                      </Tooltip>
                      <Typography.Body>{`${t('LABEL_DELIMITER_CANT_DECIMAL')}:`}</Typography.Body>
                      {renderActionsCantDecimal()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Tooltip
                        title={t('CONFIG_USERS_ABBREVIATION_INFORMATIVE_ICON_TOOLTIP_TITLE')}
                      >
                        <Typography.Icon level={2} icon={InfoCircleOutlined} />
                      </Tooltip>
                      <Typography.Body>
                        <strong>{t('LABEL_ABBREVIATIONS')}</strong>
                      </Typography.Body>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_ABBREVIATION_THOUSAND')}:`}</Typography.Body>
                      {renderActionsAbbThousand()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_ABBREVIATION_MILLION')}:`}</Typography.Body>
                      {renderActionsAbbMillon()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_ABBREVIATION_BILLION')}:`}</Typography.Body>
                      {renderActionsAbbBillon()}
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space direction="horizontal">
                      <Typography.Body>{`${t('LABEL_ABBREVIATION_TRILLION')}:`}</Typography.Body>
                      {renderActionsAbbTrillon()}
                    </Space>
                  </Col>
                </Row>
              </Space>
            </Tabs.TabPane>
          </Tabs>
        </Tabs.TabPane>
      )}
    </Tabs>
  )
}

const mapDispatchToProps = {
  editProfile: configuration.actions.editProfile,
  editUser: configuration.actions.editUser,
  assignRole: configuration.actions.assignRole,
  whoAmI: login.actions.whoAmI,
  setFormatNumbers: configuration.actions.setFormatNumbers,
  editFormatNumber: configuration.actions.editFormatNumber,
}

TabsData.propTypes = {
  pkid: PropTypes.any,
  language: PropTypes.string,
  userDetail: PropTypes.shape({
    approval_areas: PropTypes.array,
    id: PropTypes.any,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    roles: PropTypes.array,
    full_name: PropTypes.string,
    status: PropTypes.any,
    society_pk: PropTypes.any,
    society_name: PropTypes.string,
    groups: PropTypes.any,
    quicksight: PropTypes.any,
  }),
  rolesList: PropTypes.shape({
    data: PropTypes.array,
  }),
  areaList: PropTypes.array,
  userProfile: PropTypes.shape({
    phone_number: PropTypes.string,
    nickname: PropTypes.string,
    photo: PropTypes.string,
  }),
  setShowUploadModal: PropTypes.func,
  society_id: PropTypes.any,
  onRefetch: PropTypes.func,
  setShowDrawer: PropTypes.func,
  setIsSaving: PropTypes.func,
  isSaving: PropTypes.bool,
  assignRole: PropTypes.func,
  editUser: PropTypes.func,
  editProfile: PropTypes.func,
  whoAmI: PropTypes.func,
  setFormatNumbers: PropTypes.func,
  editFormatNumber: PropTypes.func,
  format_number: PropTypes.shape({
    format_enable: PropTypes.bool,
    id: PropTypes.any,
    delimiter_thousand: PropTypes.string,
    delimiter_decimal: PropTypes.string,
    decimal_places_to_show: PropTypes.any,
    abbreviation_thousand: PropTypes.string,
    abbreviation_million: PropTypes.string,
    abbreviation_billion: PropTypes.string,
    abbreviation_trillion: PropTypes.string,
  }),
  type: PropTypes.any,
}

export default connect(null, mapDispatchToProps)(TabsData)
